// ===============================================================
// SPECIAL ORDERS API (FIXED & STABLE)
// ===============================================================

import { ApiUrl, getMetadata } from "../index"
import { Timestamp } from "google-protobuf/google/protobuf/timestamp_pb"


// ---- GRPC PROTOS ----
import {
  ListSpecialOrdersRequest,
  GetSpecialOrderRequest,
  CreateSpecialOrderRequest,
  UpdateSpecialOrderRequest,
  DeleteSpecialOrderRequest,

  ListSpecialOrderPaymentsRequest,
  CreateSpecialOrderPaymentRequest,
  UpdateSpecialOrderPaymentRequest,
  DeleteSpecialOrderPaymentRequest
} from './special_orders_pb'

import { SpecialOrder, SpecialOrderPayment } from "./special_order_pb"
import { Money } from "../money_pb"

// ---- GRPC CLIENT ----
import { SpecialOrdersClient } from "./special_orders_grpc_web_pb"
const client = new SpecialOrdersClient(ApiUrl, null, null)

// ===============================================================
// HELPERS
// ===============================================================

const toMoney = src => {
  if (!src) return null
  const m = new Money()
  m.setValue(src.value || 0)
  m.setCurrencyName(src.currencyName)
  m.setExponent(src.exponent)
  return m
}

const toSpecialOrderModel = o => {
  const so = new SpecialOrder()

  if (o.name) so.setName(o.name)
  if (o.id) so.setId(o.id)

  if (o.customerName) so.setCustomerName(o.customerName)
  if (o.category) so.setCategory(o.category)
  if (o.displayName) so.setDisplayName(o.displayName)
  if (o.description) so.setDescription(o.description)

  if (o.clientName) so.setClientName(o.clientName)
  if (o.vin) so.setVin(o.vin)
  if (o.shipmentStatus) so.setShipmentStatus(o.shipmentStatus)

  if (o.purchasedAt) so.setPurchasedAt(toTimestamp(o.purchasedAt))
  if (o.amountStatus) so.setAmountStatus(o.amountStatus)

  if (o.vehicleAmount) so.setVehicleAmount(toMoney(o.vehicleAmount))
  if (o.shipmentAmount) so.setShipmentAmount(toMoney(o.shipmentAmount))

  if (o.storageFeeAmount) so.setStorageFeeAmount(toMoney(o.storageFeeAmount))
  if (o.storageFeeAmount2) so.setStorageFeeAmount2(toMoney(o.storageFeeAmount2))
  if (o.storageFeeAmount3) so.setStorageFeeAmount3(toMoney(o.storageFeeAmount3))
  if (o.storageFeeAmount4) so.setStorageFeeAmount4(toMoney(o.storageFeeAmount4))
  if (o.storageFeeAmount5) so.setStorageFeeAmount5(toMoney(o.storageFeeAmount5))
  if (o.totalStorageFeeAmount) so.setTotalStorageFeeAmount(toMoney(o.totalStorageFeeAmount))

  if (o.totalAmount) so.setTotalAmount(toMoney(o.totalAmount))

  if (o.status) so.setStatus(o.status)
  if (o.comment) so.setComment(o.comment)

  if (o.isArchive !== undefined) so.setIsArchive(o.isArchive)

  return so
}


const toProtoTimestamp = date => {
  if (!date) return null

  const ts = new Timestamp()
  ts.setSeconds(Math.floor(date.getTime() / 1000))
  ts.setNanos(0)
  return ts
}

const toPaymentModel = p => {
  let pay = new SpecialOrderPayment()

  if (p.name) pay.setName(p.name)
  if (p.specialOrderName) pay.setSpecialOrderName(p.specialOrderName)
  if (p.date) {
  const ts = new Timestamp()

  // case 1: redux timestamp object
  if (typeof p.date.seconds === "number") {
    ts.setSeconds(p.date.seconds)
    ts.setNanos(p.date.nanos || 0)
    pay.setDate(ts)
  }

  // case 2: JS Date
  else if (p.date instanceof Date) {
    ts.setSeconds(Math.floor(p.date.getTime() / 1000))
    ts.setNanos(0)
    pay.setDate(ts)
  }
}
  if (p.paymentTypeName) pay.setPaymentTypeName(p.paymentTypeName)
  if (p.status) pay.setStatus(p.status)
  if (p.amount) pay.setAmount(toMoney(p.amount))
  if (p.comment) pay.setComment(p.comment)

  return pay
}

const toTimestamp = src => {
  if (!src) return null

  const ts = new Timestamp()

  // case 1: already protobuf Timestamp
  if (src instanceof Timestamp) return src

  // case 2: seconds/nanos object (from redux / date picker)
  if (typeof src.seconds === "number") {
    ts.setSeconds(src.seconds)
    ts.setNanos(src.nanos || 0)
    return ts
  }

  // case 3: JS Date
  if (src instanceof Date) {
    ts.setSeconds(Math.floor(src.getTime() / 1000))
    ts.setNanos((src.getTime() % 1000) * 1e6)
    return ts
  }

  return null
}

const fromProtoTimestamp = ts => {
  if (!ts) return null
  return {
    seconds: ts.getSeconds(),
    nanos: ts.getNanos()
  }
}

const fromProtoMoney = m => {
  if (!m) return null
  return {
    value: m.getValue(),
    currencyName: m.getCurrencyName(),
    exponent: m.getExponent()
  }
}

const fromPaymentProto = p => ({
  name: p.getName(),
  specialOrderName: p.getSpecialOrderName(),
  paymentTypeName: p.getPaymentTypeName(),
  status: p.getStatus(),
  comment: p.getComment(),

  // ✅ FIX: timestamp preserved
  date: p.getDate() ? fromProtoTimestamp(p.getDate()) : null,

  // ✅ FIX: money preserved
  amount: p.getAmount() ? fromProtoMoney(p.getAmount()) : null
})
// ===============================================================
// API
// ===============================================================

const API = {
  // -------- LIST --------
  listSpecialOrders: params =>
    new Promise((resolve, reject) => {
      const req = new ListSpecialOrdersRequest()
      req.setPage(params?.page || 1)
      req.setPerPage(params?.perPage || 10)
      if (params?.search) req.setSearch(params.search)
      if (params?.category) req.setCategory(params.category)
      if (params?.isArchive !== undefined) req.setIsArchive(params.isArchive)

      if (params?.customerName) {
        req.setCustomerName(params.customerName)
      }

      client.listSpecialOrders(req, getMetadata(), (err, res) => {
        if (err) return reject(err)

        const obj = res.toObject()

        resolve({
          data: obj.ordersList || [],
          totalRows: obj.totalRows || 0,
          totalPages: obj.totalPages || 0
        })
      })
    }),

  // -------- GET --------
  getSpecialOrder: name =>
    new Promise((resolve, reject) => {
      const req = new GetSpecialOrderRequest()
      req.setName(name)

      client.getSpecialOrder(req, getMetadata(), (err, res) => {
        if (err) return reject(err)
        resolve(res.toObject())
      })
    }),

  // -------- CREATE --------
  createSpecialOrder: order =>
    new Promise((resolve, reject) => {
      const req = new CreateSpecialOrderRequest()
      const protoOrder = toSpecialOrderModel(order)

      console.log("create ", order)
      console.log("create proto object", protoOrder.toObject())

      req.setOrder(protoOrder)

      client.createSpecialOrder(req, getMetadata(), (err, res) => {
        if (err) return reject(err)
        resolve(res.toObject())
      })
    }),

  // -------- UPDATE --------
  updateSpecialOrder: order =>
    new Promise((resolve, reject) => {
      const req = new UpdateSpecialOrderRequest()
      req.setName(order.name)
      req.setOrder(toSpecialOrderModel(order))

      client.updateSpecialOrder(req, getMetadata(), (err, res) => {
        if (err) return reject(err)
        resolve(res.toObject())
      })
    }),

  // -------- DELETE --------
  deleteSpecialOrder: name =>
    new Promise((resolve, reject) => {
      const req = new DeleteSpecialOrderRequest()
      req.setName(name)

      client.deleteSpecialOrder(req, getMetadata(), err => {
        if (err) return reject(err)
        resolve(true)
      })
    }),

  // -------- PAYMENTS --------
  listPayments: specialOrderName =>
  new Promise((resolve, reject) => {
    const req = new ListSpecialOrderPaymentsRequest()
    req.setSpecialOrderName(specialOrderName)

    client.listSpecialOrderPayments(req, getMetadata(), (err, res) => {
      if (err) return reject(err)

      const payments =
        res.getPaymentsList().map(fromPaymentProto)

      resolve(payments)
    })
  }),


  createPayment: payment =>
    new Promise((resolve, reject) => {
      const req = new CreateSpecialOrderPaymentRequest()
      req.setPayment(toPaymentModel(payment))

      client.createSpecialOrderPayment(req, getMetadata(), (err, res) => {
        if (err) return reject(err)
        resolve(res.toObject())
      })
    }),

  updatePayment: ({ payment }) =>
  new Promise((resolve, reject) => {
    const req = new UpdateSpecialOrderPaymentRequest()

    // IMPORTANT
    req.setName(payment.name)
    req.setPayment(toPaymentModel(payment))

    client.updateSpecialOrderPayment(req, getMetadata(), (err, res) => {
      if (!res) return reject(err)
      resolve(res.toObject())
    })
  }),

deletePayment: paymentName =>
  new Promise((resolve, reject) => {
    const req = new DeleteSpecialOrderPaymentRequest()
    req.setName(paymentName)

    client.deleteSpecialOrderPayment(req, getMetadata(), err => {
      if (err) return reject(err)
      resolve(true)
    })
  })

    }

export default API